import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Store } from '@ngrx/store';
import { PhoneNumberInput, Providers } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { NavigatableDetailView } from 'src/app/common-components/detail/detail-view';
import { getProviders, providerSearch } from 'src/app/common-components/helpers/provider-helper';
import { ExtensionSaveModel } from '../../extension-details/models/extension-detail-save.model';
import {
   PhoneNumberDetailDeleteEvent,
   PhoneNumberDetailReleaseEvent,
   PhoneNumberDetailSaveEvent,
} from '../models/phone-number-detail-form-submit-event';
import { PhoneNumberDetailView } from '../models/phone-number-detail-view.model';
import { PhoneNumberPrivileges } from '../models/phone-number-privileges';
import { PhoneNumberDetailFormService } from '../services/phone-number-detail-form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-phone-number-detail-view',
    styleUrls: ['./phone-number-detail-view.component.scss'],
    templateUrl: './phone-number-detail-view.component.html',
    standalone: false
})
export class PhoneNumberDetailViewComponent extends NavigatableDetailView implements OnDestroy {
   get entityId(): number | null {
      return this.phoneNumber?.phoneNumberId || null;
   }

   @Input() get phoneNumber(): PhoneNumberDetailView | null {
      return this._phoneNumber;
   }

   set phoneNumber(value: PhoneNumberDetailView | null) {
      this._phoneNumber = value;
      if (value) {
         this.formService.updatePhoneNumber(value);
      }
   }

   canEdit: boolean;

   @Input() set phoneNumberPrivileges(privileges: PhoneNumberPrivileges) {
      this._phoneNumberPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.canEdit = false;
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
         this.canEdit = true;
         this.form.markAsPristine();
         this.form.get('phoneNumber')?.disable();
      }
   }

   get phoneNumberPrivileges(): PhoneNumberPrivileges {
      return this._phoneNumberPrivileges;
   }

   @Input() callState: CallState;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<PhoneNumberDetailSaveEvent>();

   @Output() numberReleased = new EventEmitter<PhoneNumberDetailReleaseEvent>();

   @Output() numberDeleted = new EventEmitter<PhoneNumberDetailDeleteEvent>();

   form: UntypedFormGroup;

   providers = getProviders();

   isFax = false;

   private _phoneNumber: PhoneNumberDetailView | null;

   private _phoneNumberPrivileges: PhoneNumberPrivileges;

   private destroyed$ = new Subject<void>();

   constructor(public formService: PhoneNumberDetailFormService, private store: Store) {
      super();
      this.form = this.formService.form;
   }

   ngOnInit(): void {
      const datePortedControl = this.form.get('datePorted');
      const isPortedControl = this.form.get('isPorted');
      const isFaxControl = this.form.get('isFax');
      this.store.select(getCurrentUserIsSystemUser).subscribe((isSystemUser) => {
         this.formService.toggleSystemFields(isSystemUser, this._phoneNumberPrivileges.canEdit);
      });

      isPortedControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((newValue) => {
         if (!this.canEdit) return;
         if (newValue === false) {
            datePortedControl?.disable();
            datePortedControl?.setValidators(null);
            datePortedControl?.setValue(null);
         } else {
            datePortedControl?.enable();
            if (!datePortedControl?.value) {
               datePortedControl?.setValue(new Date());
            }
            datePortedControl?.setValidators(Validators.required);
         }
         datePortedControl?.markAsDirty();
         datePortedControl?.updateValueAndValidity();
      });

      isFaxControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((newValue) => {
         this.isFax = newValue;
         if (!this._phoneNumber?.account?.accountId || !this.canEdit) {
            return;
         }
         if (newValue) {
            this.form.get('openExtension')?.disable();
            this.form.get('openExtension')?.setValidators(null);
            this.form.get('closedExtension')?.disable();
            this.form.get('closedExtension')?.setValidators(null);
            this.form.get('faxExtension')?.enable();
            this.form.get('faxExtension')?.setValidators(Validators.required);
         } else {
            this.form.get('faxExtension')?.disable();
            this.form.get('openExtension')?.enable();
            this.form.get('closedExtension')?.enable();
            this.form.get('faxExtension')?.setValidators(null);
         }
         this.form.get('openExtension')?.markAsDirty();
         this.form.get('openExtension')?.updateValueAndValidity();
         this.form.get('closedExtension')?.markAsDirty();
         this.form.get('closedExtension')?.updateValueAndValidity();
         this.form.get('faxExtension')?.markAsDirty();
         this.form.get('faxExtension')?.updateValueAndValidity();
      });

      const isActiveControl = this.form.get('isActive');

      isActiveControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((isActive) => {
         const openExt = this.form.get('openExtension');
         const closedExt = this.form.get('closedExtension');

         if (!this.canEdit) return;

         if (isActive && this._phoneNumber?.account?.accountId) {
            openExt?.setValidators(Validators.required);
            closedExt?.setValidators(Validators.required);
         } else {
            openExt?.clearValidators();
            closedExt?.clearValidators();
         }

         openExt?.updateValueAndValidity({ emitEvent: false });
         closedExt?.updateValueAndValidity({ emitEvent: false });
      });

      isActiveControl?.setValue(isActiveControl.value);
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   canRelease(): boolean {
      if (this._phoneNumber?.account?.accountId) {
         return this._phoneNumberPrivileges.canAdd && this._phoneNumberPrivileges.canEdit;
      } else {
         return false;
      }
   }

   canDelete(): boolean {
      return this._phoneNumberPrivileges.canDelete;
   }

   onReleaseAccountClick(): void {
      const releaseEventModel: PhoneNumberDetailReleaseEvent = {
         phoneNumber: {
            phoneNumberId: this.phoneNumber?.phoneNumberId ?? null,
         },
         phonesUsingForCallerId: (this.phoneNumber?.phonesUsingForCallerId as ExtensionSaveModel[]) ?? [],
         faxesUsingForCallerId: (this.phoneNumber?.faxesUsingForCallerId as ExtensionSaveModel[]) ?? [],
      };

      this.numberReleased.emit(releaseEventModel);
   }

   onDeleteClick(): void {
      const deleteEventModel: PhoneNumberDetailDeleteEvent = {
         phoneNumber: {
            phoneNumberId: this.phoneNumber?.phoneNumberId ?? null,
         },
         phonesUsingForCallerId: (this.phoneNumber?.phonesUsingForCallerId as ExtensionSaveModel[]) ?? [],
         faxesUsingForCallerId: (this.phoneNumber?.faxesUsingForCallerId as ExtensionSaveModel[]) ?? [],
      };

      this.numberDeleted.emit(deleteEventModel);
   }

   providerDisplayWith(option: Providers): string {
      return `${option ?? ''}`;
   }

   providerSearch(searchText: string) {
      this.providers = providerSearch(searchText);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   providerReset() {
      this.providers = getProviders();
   }

   private getFormSubmittedEvent(): PhoneNumberDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.phoneNumber) {
         return null;
      }

      const phoneNumber = this.formService.getValue(this.phoneNumber.phoneNumberId);
      let phoneNumberFields: (keyof PhoneNumberInput)[] = [];
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'account', 'accountId');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'allowOutboundCalls');
      phoneNumberFields = this.formService.addFieldForDirtyControl(
         phoneNumberFields,
         'closedExtension',
         'closedExtensionId'
      );
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'datePorted');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'displayName');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'faxExtension', 'faxExtensionId');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'isActive');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'isPorted');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'location', 'locationId');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'timeZone', 'timeZoneId');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'notes');
      phoneNumberFields = this.formService.addFieldForDirtyControl(
         phoneNumberFields,
         'openExtension',
         'openExtensionId'
      );
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'phoneNumber');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'providerId');
      phoneNumberFields = this.formService.addFieldForDirtyControl(phoneNumberFields, 'schedule', 'scheduleId');
      return { phoneNumber, phoneNumberFields };
   }
}
